import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocale, useService } from "../../../../hooks";
import { NavigatorContext } from "../../../../context";

export const useAddApp = () => {
  const { t } = useLocale();
  const { navigateTo } = useContext(NavigatorContext);
  const { setTitle } = useService("Meta");
  const { fetchByAttr, save, update } = useService("Data", "app");
  const { urlToSplitData, getLocationData } = useService("Router");
  const { getLoggedInUserData, getUser } = useService("User");
  const { generateRandomString, generateUID, showAlert } = useService("Misc");
  const user = getLoggedInUserData();
  const [activeBuilder, setactiveBuilder] = useState(null);
  const [mode, setmode] = useState<"add" | "edit" | "view">("add");
  const [uid, setuid] = useState(new Date().getTime());
  const [id, setid] = useState(
    generateRandomString(8 + Math.ceil(Math.random() * 8))
  );
  const [secret, setsecret] = useState(
    generateRandomString(24 + Math.ceil(Math.random() * 8))
  );
  const [allowedApis, setallowedApis] = useState([]);
  const [builders, setbuilders] = useState([]);
  const [status, setstatus] = useState("active");
  const [title, settitle] = useState<string>("");
  const [brandKey, setbrandKey] = useState<string>("");
  const [description, setdescription] = useState<string>("");
  let [author, setauthor] = useState(
    ["admin", "developer"].includes(user.role) ? "" : user.uid
  );

  const getBuilders = useCallback(
    async (author = false) => {
      const result = await getUser({ role: "builder" });
      let c = [];
      if (result.status) {
        result.data.forEach((e) => {
          let el = {
            label: e.name,
            value: e.uid,
          };
          c.push(el);
          if (author) {
            if (author === e.uid) {
              setactiveBuilder(el);
            }
          }
        });
        setbuilders(c);
      }
    },
    [getUser]
  );
  const getAppData = useCallback(
    async (query) => {
      const result = await fetchByAttr(query);
      if (result.status && result.data?.length > 0) {
        let app = result.data[0];
        setid(app.id);
        setuid(app.uid);
        setsecret(app.secret);
        setallowedApis(app.allowedApis);
        setstatus(app.status);
        setauthor(app.author);
        settitle(app.title);
        setbrandKey(app.brandKey);
        setdescription(app.description);
        if (["admin", "developer"].includes(user.role)) {
          getBuilders(app.author);
        }
      }
    },
    [fetchByAttr, getBuilders, user.role]
  );

  const addApp = useCallback(
    async (e) => {
      e.preventDefault();
      let error = false,
        errorMsg = "";
      let appObject = {
        title,
        brandKey,
        id,
        secret,
        description,
        author: activeBuilder !== null ? activeBuilder.value : author,
        status,
        allowedApis: JSON.stringify(allowedApis),
        uid: mode === "add" ? generateUID() : uid,
      };
      if (title === "") {
        errorMsg = t("errorMsg.required.title");
        error = true;
      } else if (brandKey === "") {
        errorMsg = t("errorMsg.required.brandKey");
        error = true;
      } else if (author === "") {
        errorMsg = t("errorMsg.required.author");
        error = true;
      }
      if (!error) {
        if (mode === "add") {
          const result = await save(appObject);
          if (result.status) {
            showAlert({ type: "success", msg: t("success.addApp") });
            navigateTo({ route: "/admin/app/list" });
          } else {
            showAlert({ type: "error", msg: t("errorMsg.fail.addApp") });
          }
        } else {
          let data = {
            query: JSON.stringify({ uid }),
            appData: JSON.stringify(appObject),
          };
          const result = await update(data);
          if (result.status) {
            showAlert({ type: "success", msg: t("success.updateApp") });
            navigateTo({ route: "/admin/app/list" });
          } else {
            showAlert({ type: "error", msg: t("errorMsg.fail.updateApp") });
          }
        }
      } else showAlert({ type: "error", msg: errorMsg });
    },
    [
      activeBuilder,
      allowedApis,
      author,
      brandKey,
      description,
      generateUID,
      id,
      mode,
      navigateTo,
      save,
      secret,
      showAlert,
      status,
      t,
      title,
      uid,
      update,
    ]
  );

  const assignBuilder = useCallback((e) => {
    setauthor(e.value);
    setactiveBuilder(e);
  }, []);

  const toggleStatus = useCallback(() => {
    setstatus(status === "active" ? "inactive" : "active");
  }, [status]);

  const urlData = urlToSplitData(getLocationData().pathname);
  const urlMode = urlData[3];
  const urlUid = urlData[4];

  useEffect(() => {
    const mode = "add";
    if (urlMode === "edit") {
      setmode("edit");
      getAppData({
        uid: urlUid,
      });
    } else {
      if (["admin", "developer"].includes(user.role)) {
        getBuilders();
      }
    }
    setTitle(mode + " App", "admin");
  }, [getAppData, getBuilders, setTitle, urlMode, urlUid, user.role]);
  return {
    builders,
    mode,
    addApp,
    title,
    settitle,
    description,
    setdescription,
    id,
    setid,
    secret,
    setsecret,
    toggleStatus,
    user,
    status,
    brandKey,
    setbrandKey,
    activeBuilder,
    assignBuilder,
  };
};
